'use client'

import { useState } from 'react'
import {
  detectVideoProvider,
  buildVideoEmbedSrc,
  aspectRatioFor,
  type EmbedOptions,
} from '@sifly/shared-utils'
import { EmbedFallbackCard } from './EmbedFallbackCard'

const PROVIDER_LABEL = {
  youtube: 'YouTube',
  vimeo: 'Vimeo',
  wistia: 'Wistia',
} as const

/**
 * Facade-pattern (click-to-load) video embed. Renders a poster image
 * with a play-button overlay; only injects the provider iframe after
 * the user clicks, with autoplay=1. Zero player JS or third-party
 * cookies until the user expresses intent — substantial LCP / INP win
 * vs an eager iframe.
 *
 * Poster sources, in precedence order:
 *   1. options.poster_url — custom override (URL or, eventually, a
 *      media-library asset chosen by the author).
 *   2. resolvedPoster — server-fetched oEmbed thumbnail (Vimeo, Wistia).
 *   3. YouTube's predictable thumbnail URL with maxres → hq fallback.
 *   4. Gradient placeholder if all of the above fail.
 *
 * Falls back to a clean link card on iframe error or unparseable URL.
 */
export function VideoEmbedSection({
  url,
  options,
  resolvedPoster,
}: {
  url: string
  options?: EmbedOptions
  resolvedPoster?: string
}) {
  const detected = detectVideoProvider(url)
  const [playing, setPlaying] = useState(false)
  const [iframeErrored, setIframeErrored] = useState(false)
  const [posterErrored, setPosterErrored] = useState(false)

  if (!detected) {
    return (
      <EmbedFallbackCard
        url={url}
        providerLabel="Video"
        reason="This URL doesn't look like YouTube, Vimeo, or Wistia."
      />
    )
  }

  if (iframeErrored) {
    return (
      <EmbedFallbackCard url={url} providerLabel={PROVIDER_LABEL[detected.provider]} />
    )
  }

  const aspect = aspectRatioFor(detected)
  const customPoster =
    typeof options?.poster_url === 'string' && options.poster_url
      ? (options.poster_url as string)
      : null
  const youtubePoster =
    detected.provider === 'youtube'
      ? `https://i.ytimg.com/vi/${detected.id}/maxresdefault.jpg`
      : null
  const poster = customPoster ?? resolvedPoster ?? youtubePoster ?? null

  // Build the playing-iframe src with autoplay forced on (since the
  // click is the user gesture, browsers honor autoplay reliably).
  const playSrc = (() => {
    const o: EmbedOptions = { ...(options ?? {}), autoplay: true }
    return buildVideoEmbedSrc(detected, o)
  })()

  return (
    <div className="not-prose my-6 mx-auto w-full max-w-[var(--section-max-width,48rem)]">
      <div
        className="relative w-full overflow-hidden rounded-lg bg-black"
        style={{ aspectRatio: aspect }}
      >
        {playing ? (
          <iframe
            src={playSrc}
            title={`${PROVIDER_LABEL[detected.provider]} video`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            onError={() => setIframeErrored(true)}
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play ${PROVIDER_LABEL[detected.provider]} video`}
            className="group absolute inset-0 h-full w-full cursor-pointer"
          >
            {poster && !posterErrored ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={poster}
                alt=""
                loading="lazy"
                onError={e => {
                  // YouTube fallback: maxresdefault doesn't exist for
                  // every video — fall back to hqdefault, then to a
                  // gradient placeholder if that 404s too.
                  if (
                    detected.provider === 'youtube' &&
                    !customPoster &&
                    !resolvedPoster &&
                    e.currentTarget.src.includes('maxresdefault.jpg')
                  ) {
                    e.currentTarget.src = `https://i.ytimg.com/vi/${detected.id}/hqdefault.jpg`
                    return
                  }
                  setPosterErrored(true)
                }}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
            ) : (
              <div
                className="h-full w-full"
                style={{
                  background:
                    'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
                }}
              />
            )}
            <span className="absolute inset-0 flex items-center justify-center">
              <span
                className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-lg transition group-hover:bg-white group-hover:scale-105"
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  fill="#111"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7L8 5z" />
                </svg>
              </span>
            </span>
          </button>
        )}
      </div>
    </div>
  )
}
